import { Button, Modal } from "rizzui";
import { useFormik } from "formik";
import { useState } from "react";
import { CensusService } from "../../../../services/census/censusService";
import { toast } from "sonner";

interface ImportCensusModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    mode?: 'census' | 'ofc';
}

export const ImportCensusModal: React.FC<ImportCensusModalProps> = ({ isOpen, onClose, refetch, mode = 'census' }) => {
    const [loading, setLoading] = useState(false);

    const handleImport = async (values: any) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', values.file);

            const isOfc = mode === 'ofc';
            const service = new CensusService();
            const importPromise = isOfc ? service.importOfc(formData) : service.importCensus(formData);

            toast.promise(
                importPromise,
                {
                    loading: isOfc ? 'Importando OFC...' : 'Importando censos...',
                    success: () => {
                        refetch();
                        return isOfc ? 'OFC importados correctamente' : 'Censos importados correctamente';
                    },
                    error: (error) => {
                        console.error('Error importing:', error);
                        return isOfc ? 'Error importando OFC' : 'Error importando censos';
                    },
                }
            );

            onClose();
        }
        catch (error) {
            console.error('Error importing census:', error);
        } finally {
            setLoading(false);
        }
    }

    const formik = useFormik({
        initialValues: {
            file: null,
        },
        onSubmit: (values) => {
            handleImport(values);
        },
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='sm'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-xl !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='flex items-center justify-between p-4'>
                <h2 className='text-lg font-semibold text-gray-800 dark:text-white'>
                    {mode === 'ofc' ? 'Importar OFC desde un excel' : 'Importar censos desde un excel'}
                </h2>
                <button onClick={onClose} className='text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className='m-auto px-7 pt-6 pb-4'>
                <form onSubmit={formik.handleSubmit}>
                    <div className='mb-4'>
                        <label htmlFor="file" className='block text-sm font-medium text-gray-700 dark:text-white'>
                            {mode === 'ofc' ? 'Selecciona el archivo excel para importar OFC' : 'Selecciona el archivo excel para importar censos'}
                        </label>
                        <input
                            type="file"
                            id="file"
                            name="file"
                            accept=".xlsx, .xls"
                            onChange={(event) => {
                                const file = event?.currentTarget?.files ? event.currentTarget.files[0] : null;
                                formik.setFieldValue("file", file);
                            }}
                            required
                            className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700'
                        />
                    </div>
                </form>
            </div>

            <div className='flex justify-center p-7'>
                <Button
                    onClick={() => formik.handleSubmit()}
                    color='primary'
                    disabled={loading}
                >
                    Importar
                </Button>
            </div>
        </Modal>
    );
};
